// Mạng lưới của Đại lý / CTV (GĐ4): cấp dưới, tạo cấp dưới, thư viện bảng giá, link giới thiệu.
//  · Đại lý thấy CTV + Shop của mình + Shop của CTV mình; CTV thấy Shop của mình (§2.8).
//  · Sơ đồ: máy tính là sơ đồ theo NHÓM chiếm hết màn (đổi 31/10, như /npp/network) — ô gốc là chính mình nối ra khối
//    CTV / Shop, khung bỏ menu trái + thanh trên (☰ ở hàng đầu trang mở menu đè), dải thống kê ở trên, nút ⓘ mở ngăn gọn
//    bên phải (DownlinePanel — Đại lý / CTV không có trang Đơn vị nên không có cài đặt cấp dưới ở đây). Điện thoại /
//    iPad: cây THỤT LỀ + ngăn từ đáy, cùng nội dung. Trạng thái cây (chọn, lọc, ?unit=) như /npp/network
//    (components/network/useNetworkTreeState.ts).
//  · Thêm tài khoản (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md): một ngăn "Thêm tài khoản" (?them=1 — nút góc của khung mở tới
//    đây) có hai cách: Tự tạo (Đại lý tạo CTV hoặc Shop; CTV tạo Shop) và Link giới thiệu + mã QR (InviteLinkPanel).
//    Bảng giá / bảng chiết khấu: Có sẵn · Tạo mới (hộp lập bảng đè lên form) · Gán sau (chưa lên đơn được — bảng giá;
//    chưa được tính chiết khấu — bảng chiết khấu). Link giới thiệu mặc định Gán sau.
//  · Đại lý tạo CTV kèm bảng chiết khấu: xem trước trần chiết khấu theo bảng giá CTV đã chọn (D25); máy chủ gán ngay.
//  · Giao diện của CTV (Quản lý / Bán hàng — docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md): chỉ chọn khi tạo, không sửa
//    về sau; CTV chỉ có một giao diện thì ngăn chi tiết ghi rõ.

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { buildNetworkTree, networkCountsText, networkRows, networkUnitTitle, type NetworkNode } from '@shoptrack/common/network-tree'
import { get, post } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { useProfileIncompleteHandler } from '@/lib/onboarding'
import { isValidVnPhone, normalizePhoneVN } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { PhoneInput } from '@/components/ui/phone-input'
import { Sheet } from '@/components/ui/sheet'
import { SkeletonRows } from '@/components/ui/skeleton'
import { LoadError } from '@/components/ui/data-notice'
import { WidePageHeader } from '@/components/layout/wide-shell'
import { cleanShopName, useShopSettings } from '@/components/layout/shell-model'
import { useUnitCan } from '@/lib/unitAccess'
import { hasAccess } from '@/lib/permissions'
import { MODE_SHORT, currentMonthVN, tiersSummary, versionAt, vnd, type CapCheck, type Scheme } from '@/components/commissions/types'
import { ALL_WORK_MODES, WorkModePicker } from '@/components/network/WorkModePicker'
import { Segmented } from '@/components/ui/segmented'
import { InviteLinkPanel } from '@/components/network/InviteLinkPanel'
import { SetupPicker, defaultSetup, priceLaterWarning, schemeLaterWarning, setupValue, type SetupChoice } from '@/components/network/SetupPicker'
import { NewPriceListDialog, NewSchemeDialog } from '@/components/price-lists/NewListDialog'
import { NetworkCanvas, type NetworkCardView } from '@/components/network/NetworkCanvas'
import { NetworkRootSummary, NetworkSheet, NetworkSidePanel, useNetworkPanelEscape } from '@/components/network/NetworkPanel'
import { NetworkStatStrip, type NetworkStatTile } from '@/components/network/NetworkStatStrip'
import { NetworkToolbar } from '@/components/network/NetworkToolbar'
import { NETWORK_TYPE_META, NetworkRowItem, networkTypeMeta } from '@/components/network/NetworkTreeRow'
import { networkDownlineIssues, networkUnderRoot } from '@/components/network/network-view'
import { useNetworkTreeState } from '@/components/network/useNetworkTreeState'
import { DownlinePanel, downlineSub, type NetworkUnit } from './DownlinePanel'

type UnitType = 'agency' | 'ctv' | 'shop'

interface PriceListOpt {
  id: string
  code: string
  name: string
  kind: 'ctv' | 'shop'
  unitCount: number
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const TYPE_LABEL: Record<UnitType, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

/** Khoá ô gốc (chính mình) trên cây ngang — không trùng mã đơn vị. */
const ROOT = '__me'
/** Ngăn chi tiết cấp dưới (bản gọn). */
const PANEL_W = 392
const PLACEHOLDER = 'Tìm tên, SĐT…'

const hasIssue = (u: NetworkUnit) => networkDownlineIssues(u).length > 0
// Như danh sách cũ: tìm cả tên cấp trên — gõ tên CTV là ra luôn các Shop của CTV đó
const searchFields = (u: NetworkUnit) => [u.owner?.name, u.owner?.phone, u.parentName]

export default function NetworkPage() {
  const auth = getAuthState()
  const me = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const myType = (me?.tenantType ?? 'shop') as UnitType
  const { data: settings } = useShopSettings(auth.activeShopId)
  const myName = cleanShopName(settings?.displayName || me?.name || '') || TYPE_LABEL[myType]
  const childTypes: Array<'ctv' | 'shop'> = myType === 'agency' ? ['ctv', 'shop'] : ['shop']
  const isMobile = useIsMobile()
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): tạo cấp dưới / link giới thiệu cần "Tạo cấp dưới"; chọn bảng giá
  // cho link + gán / lập bảng giá, bảng chiết khấu cần "Bảng giá & hoa hồng cấp dưới"; duyệt nâng cấp chỉ chủ đơn vị
  const { can, isStaff } = useUnitCan()
  const canCreate = can('network.manage')
  const canPricing = can('network.pricing')
  // Xem đơn của một cấp dưới: trang Đơn hàng của chế độ Quản lý — canh như route (RequireRole path /agency | /ctv)
  const ordersGuard = myType === 'agency' ? '/agency' : '/ctv'
  const ordersPath = can('orders.view') && hasAccess(me?.role, ordersGuard, me?.tenantType, me?.staff, me?.workModes) ? `${ordersGuard}/orders` : null
  // Ngăn Thêm tài khoản theo ?them=1 — nút góc của khung (shell-model primaryAction) mở thẳng tới đây
  const [params, setParams] = useSearchParams()
  const adding = canCreate && params.get('them') === '1'
  const setAdding = (on: boolean) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (on) next.set('them', '1')
        else next.delete('them')
        return next
      },
      { replace: true },
    )
  const canUpgrade = myType === 'agency' && !isStaff
  // Gửi thông báo cho cấp dưới (docs/KE_HOACH_GUI_THONG_BAO.md): Đại lý, CTV; nhân viên cần "Gửi thông báo"
  const canAnnounce = (myType === 'agency' || myType === 'ctv') && can('network.announce')

  const units = useQuery({ queryKey: ['network-units'], queryFn: () => get<{ units: NetworkUnit[]; selfCode?: string | null }>('/network/units') })
  // Yêu cầu nâng cấp của cấp dưới chờ Đại lý duyệt (docs/KE_HOACH_NANG_CAP_DON_VI.md §3.7)
  const upgradeQ = useQuery({
    queryKey: ['upgrade', 'approvals', false],
    queryFn: () => get<{ requests: { id: string }[] }>('/upgrade/approvals?status=pending'),
    enabled: myType === 'agency' && !isStaff,
    retry: false,
  })
  const pendingUpgrades = upgradeQ.data?.requests.length ?? 0
  const lists = useQuery({ queryKey: ['network-price-lists'], queryFn: () => get<{ priceLists: PriceListOpt[] }>('/network/price-lists'), enabled: canCreate || canPricing })
  // Bảng chiết khấu của Đại lý (chọn khi tạo CTV / cho link CTV)
  const withSchemes = myType === 'agency' && canPricing
  const schemesQ = useQuery({
    queryKey: ['commissions', 'schemes', 'active'],
    queryFn: () => get<{ schemes: Scheme[] }>('/commissions/schemes'),
    enabled: withSchemes && adding,
  })
  const schemes = withSchemes ? (schemesQ.data?.schemes ?? []).filter((x) => !x.archivedAt) : null

  // Cấp dưới trực tiếp (parId = mình) treo dưới ô gốc
  const tree = useMemo(() => buildNetworkTree(networkUnderRoot(units.data?.units ?? [], auth.activeShopId)), [units.data, auth.activeShopId])
  const t = useNetworkTreeState(tree, { rootKey: ROOT, desktop: !isMobile, hasIssue, searchFields })
  useNetworkPanelEscape(!isMobile && !!t.selKey, t.close)

  const total = tree.byId.size
  const totalText = `${total.toLocaleString('vi-VN')} đơn vị`
  // Ô gốc là chính mình: mã của mình trong NPP (DL02…), chưa có thì chữ tắt loại
  const rootShort = units.data?.selfCode ?? NETWORK_TYPE_META[myType === 'agency' ? 'agency' : 'ctv'].short
  const emptyText = t.filter === 'issues' && !t.text ? 'Không có đơn vị nào cần xử lý.' : 'Không có đơn vị nào khớp.'

  // ─── Dải thống kê ────────────────────────────────────────────
  // CTV chỉ có Shop → ô Shop không lọc được (lọc ra đúng cả cây)
  // Nhân viên không có quyền xem đơn: API trả orders30d = null → bỏ số đơn khỏi dải thống kê
  const seeOrders = (units.data?.units ?? []).every((u) => typeof u.orders30d === 'number')
  const typeTiles: NetworkStatTile[] = childTypes.map((type) => ({
    key: type,
    label: NETWORK_TYPE_META[type].label,
    value: t.stats.counts[type],
    sub: type === 'shop' && seeOrders ? `${t.stats.shopsWithOrders.toLocaleString('vi-VN')} có đơn` : undefined,
    badge: { short: NETWORK_TYPE_META[type].short, className: cn(NETWORK_TYPE_META[type].tone, 'bg-toneBg text-toneFg') },
    ...(childTypes.length > 1 ? { onClick: () => t.pickFilter(type), active: t.filter === type } : {}),
  }))
  const tiles: NetworkStatTile[] = [
    ...typeTiles,
    ...(seeOrders ? [{ key: 'orders', label: 'Đơn 30 ngày', value: t.stats.orders30d }] : []),
    {
      key: 'issues',
      label: 'Cần xử lý',
      value: t.stats.issues,
      badge: { short: '!', className: 'bg-warn/10 text-warn' },
      onClick: () => t.pickFilter('issues'),
      active: t.filter === 'issues',
      warn: t.stats.issues > 0,
    },
  ]

  const toolbar = (compact: boolean) => (
    <NetworkToolbar
      compact={compact}
      text={t.text}
      onText={t.setText}
      placeholder={PLACEHOLDER}
      hits={t.hits}
      filter={t.filter}
      onFilter={t.setFilter}
      issueCount={t.stats.issues}
      filtering={t.filtering}
      disabled={!total}
      allOpen={t.allOpen}
      onExpandAll={t.expandAll}
      onCollapseAll={t.collapseAll}
      drill={t.drill}
      rootName={myName}
    />
  )

  const panelBody = (compact: boolean) =>
    t.shownKey === ROOT ? (
      <NetworkRootSummary
        short={rootShort}
        name={myName}
        kind={TYPE_LABEL[myType]}
        counts={tree.counts}
        types={childTypes}
        issueCount={t.stats.issues}
        onIssues={() => t.setFilter('issues')}
        onClose={t.close}
        compact={compact}
      />
    ) : t.shownNode ? (
      <DownlinePanel
        key={t.shownNode.unit.id}
        node={t.shownNode}
        rootKey={ROOT}
        rootName={myName}
        onPick={t.select}
        onClose={t.close}
        inTree={(id) => tree.byId.has(id)}
        compact={compact}
        canPricing={canPricing}
        isOwner={!isStaff}
        ordersPath={ordersPath}
      />
    ) : null

  // Ngăn Thêm tài khoản: điện thoại trượt từ đáy, máy tính ngăn phải (Sheet tự đổi)
  const addSheet = canCreate && (
    <Sheet open={adding} onOpenChange={(v) => !v && setAdding(false)} title="Thêm tài khoản" className="sm:max-w-[460px]" focusContent>
      {adding && (
        <AddUnitPanel
          childTypes={childTypes}
          priceLists={lists.data?.priceLists ?? []}
          schemes={schemes}
          schemesError={schemesQ.isError}
          canPricing={canPricing}
          onDone={() => setAdding(false)}
        />
      )}
    </Sheet>
  )
  const noUnits = <p className="px-3 py-8 text-center text-sm text-muted">Chưa có cấp dưới.</p>
  const upgradeBadge = pendingUpgrades > 0 && (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold tabular-nums text-onAccent">
      {pendingUpgrades.toLocaleString('vi-VN')}
    </span>
  )

  if (isMobile) {
    const rows = networkRows(tree.roots, t.open, t.match)
    return (
      <div className="space-y-3">
        {/* Thêm tài khoản: nút góc của khung (Glass: nút tròn ở dock; Gốc: nút trên thanh đầu) */}
        <PageHeader title="Hệ thống" />

        {(canUpgrade || canAnnounce) && (
          <div className={cn('grid gap-2', canUpgrade && canAnnounce && 'grid-cols-2')}>
            {canUpgrade && (
              <Link
                to="/network/upgrade-requests"
                className="motion-state flex min-h-11 items-center justify-center gap-2 rounded-[12px] bg-accent/10 px-2.5 text-sm font-medium text-accentSoft hover:bg-accent/15"
              >
                Duyệt nâng cấp
                {upgradeBadge}
              </Link>
            )}
            {canAnnounce && (
              <Link
                to="/network/announcements"
                className="motion-state flex min-h-11 items-center justify-center rounded-[12px] border border-rule bg-raise px-2.5 text-sm font-medium text-ink hover:bg-paperSoft"
              >
                Gửi thông báo
              </Link>
            )}
          </div>
        )}

        {units.data && <NetworkStatStrip tiles={tiles} />}

        <section className="min-w-0 rounded-2xl bg-raise shadow-card dark:border dark:border-rule">
          {toolbar(true)}
          {units.isLoading ? (
            <SkeletonRows rows={6} className="p-3" />
          ) : units.isError ? (
            <LoadError onRetry={() => units.refetch()} />
          ) : (
            <ul className="p-2 pb-4" aria-label={`Mạng lưới ${myName}`}>
              {/* Gốc = chính mình: tâm ô gốc thẳng cột đường kẻ đầu tiên để nhánh mọc ra từ nó */}
              <li className="relative flex min-h-12 items-stretch">
                {rows.length > 0 && <span aria-hidden className="absolute bottom-0 left-3 top-[calc(50%+0.875rem)] border-l-[1.5px] border-muted/35 mobile:left-2.5" />}
                <button
                  type="button"
                  onClick={t.close}
                  aria-current={!t.sel || undefined}
                  className="motion-state flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1.5 pr-2.5 text-left hover:bg-paperSoft/70"
                >
                  <span aria-hidden className="relative -ml-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-accent text-[0.625rem] font-bold text-onAccent mobile:-ml-1">
                    {rootShort}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-semibold text-ink">{myName}</span>
                    <span className="truncate text-xs text-muted">{networkCountsText(tree.counts)}</span>
                  </span>
                </button>
              </li>
              {rows.map((row) => (
                <NetworkRowItem
                  key={row.node.unit.id}
                  row={row}
                  selected={row.node === t.sel}
                  compact
                  issues={networkDownlineIssues(row.node.unit)}
                  sub={downlineSub(row.node)}
                  onToggle={t.toggle}
                  onSelect={t.select}
                />
              ))}
              {rows.length === 0 && total > 0 && <li className="px-3 py-8 text-center text-sm text-muted">{emptyText}</li>}
              {total === 0 && <li>{noUnits}</li>}
            </ul>
          )}
        </section>

        {addSheet}

        <NetworkSheet open={!!t.selKey} onClose={t.close} label={t.sel ? t.sel.unit.name : myName}>
          {panelBody(true)}
        </NetworkSheet>
      </div>
    )
  }

  // ─── Máy tính: sơ đồ theo nhóm ───────────────────────────────
  const describe = (node: NetworkNode<NetworkUnit>): NetworkCardView => {
    const u = node.unit
    const issues = networkDownlineIssues(u)
    const meta = networkTypeMeta(u.tenantType)
    const sub = downlineSub(node)
    return {
      short: u.code ?? meta.short,
      tone: meta.tone,
      name: u.name,
      sub,
      dot: issues.some((i) => i.severity === 'danger') ? 'danger' : issues.length ? 'warn' : null,
      label: `${u.code ? `${u.code} ` : ''}${networkUnitTitle(u)}, ${sub}${issues.length ? `, ${issues.map((i) => i.label).join(', ')}` : ''}`,
    }
  }
  const btn = 'motion-state inline-flex h-10 items-center gap-2 rounded-[10px] px-3.5 text-sm font-medium'

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3.5">
      <WidePageHeader
        title="Hệ thống"
        actions={
          (canUpgrade || canAnnounce || canCreate) && (
            <>
              {canUpgrade && (
                <Link to="/network/upgrade-requests" className={cn(btn, 'bg-accent/10 text-accentSoft hover:bg-accent/15')}>
                  Duyệt nâng cấp
                  {upgradeBadge}
                </Link>
              )}
              {canAnnounce && (
                <Link to="/network/announcements" className={cn(btn, 'border border-rule bg-raise text-ink hover:bg-paperSoft')}>
                  Gửi thông báo
                </Link>
              )}
              {/* Trang cây ngang bỏ thanh trên của khung → nút Thêm tài khoản nằm ở đây */}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  aria-expanded={adding}
                  aria-haspopup="dialog"
                  className={cn(btn, 'bg-accent px-4 font-semibold text-onAccent hover:bg-accentSoft')}
                >
                  Thêm tài khoản
                </button>
              )}
            </>
          )
        }
      />
      {units.data && <NetworkStatStrip tiles={tiles} />}
      <section
        aria-label={`Mạng lưới ${myName}`}
        className="flex min-h-[360px] flex-1 flex-col overflow-hidden rounded-xl border border-rule bg-raise shadow-card"
      >
        {toolbar(false)}
        {units.isLoading ? (
          <SkeletonRows rows={6} className="p-3" />
        ) : units.isError ? (
          <LoadError onRetry={() => units.refetch()} />
        ) : total === 0 ? (
          noUnits
        ) : (
          <NetworkCanvas
            drill={t.drill}
            selected={t.selKey}
            root={{ short: rootShort, name: myName, sub: totalText, label: `${myName}, ${totalText}` }}
            describe={describe}
            hasIssue={hasIssue}
            showOrders={seeOrders}
            emptyText={emptyText}
            panelWidth={t.selKey ? PANEL_W : 0}
            panel={
              <NetworkSidePanel open={!!t.selKey} width={PANEL_W}>
                {panelBody(false)}
              </NetworkSidePanel>
            }
          />
        )}
      </section>

      {addSheet}
    </div>
  )
}

/** Ngăn Thêm tài khoản: Tự tạo · Link giới thiệu. */
function AddUnitPanel({
  childTypes,
  priceLists,
  schemes,
  schemesError,
  canPricing,
  onDone,
}: {
  childTypes: Array<'ctv' | 'shop'>
  priceLists: PriceListOpt[]
  /** null = không chọn được bảng chiết khấu (CTV, hoặc nhân viên không có quyền Bảng giá) */
  schemes: Scheme[] | null
  schemesError: boolean
  canPricing: boolean
  onDone: () => void
}) {
  const [tab, setTab] = useState<'create' | 'link'>('create')
  return (
    <div className="space-y-5">
      <Segmented
        value={tab}
        onChange={setTab}
        options={[
          ['create', 'Tự tạo'],
          ['link', 'Link giới thiệu'],
        ]}
        label="Cách thêm tài khoản"
        className="w-full text-sm [&>button]:flex-1"
      />
      {tab === 'create' ? (
        <CreateUnitForm childTypes={childTypes} priceLists={priceLists} schemes={schemes} schemesError={schemesError} canPricing={canPricing} onDone={onDone} />
      ) : (
        <InviteLinkPanel
          types={childTypes}
          path="/network/invites"
          api={{ get, post }}
          queryKey={['network-invites']}
          priceLists={priceLists}
          schemes={schemes}
          canChoosePrice={canPricing}
        />
      )}
    </div>
  )
}

function CreateUnitForm({
  childTypes,
  priceLists,
  schemes,
  schemesError,
  canPricing,
  onDone,
}: {
  childTypes: Array<'ctv' | 'shop'>
  priceLists: PriceListOpt[]
  /** Đại lý (có quyền Bảng giá): bảng chiết khấu chọn khi tạo CTV; null = không có ô chiết khấu */
  schemes: Scheme[] | null
  schemesError: boolean
  /** Lập bảng mới (Tạo mới) cần quyền Bảng giá & chiết khấu cấp dưới */
  canPricing: boolean
  onDone: () => void
}) {
  const qc = useQueryClient()
  const optionsOf = (t: 'ctv' | 'shop') => priceLists.filter((l) => l.kind === t)
  const [form, setForm] = useState(() => ({
    unitType: childTypes[0]!,
    name: '',
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    password: '',
    workModes: ALL_WORK_MODES,
  }))
  // null = chưa tự chọn → mặc định theo thư viện (thư viện có thể tải xong sau khi mở form)
  const [pickedPrice, setPrice] = useState<SetupChoice | null>(null)
  const [pickedScheme, setScheme] = useState<SetupChoice | null>(null)
  // Hộp lập bảng mới đang mở (đè lên form)
  const [making, setMaking] = useState<'price' | 'scheme' | null>(null)
  const options = optionsOf(form.unitType)
  const price = pickedPrice ?? defaultSetup(options.length > 0, canPricing)
  const scheme = pickedScheme ?? defaultSetup((schemes?.length ?? 0) > 0, canPricing)
  const withScheme = schemes !== null && form.unitType === 'ctv'
  const chosenScheme = scheme.mode === 'pick' ? schemes?.find((x) => x.id === scheme.id) ?? null : null
  const priceId = price.mode === 'pick' ? price.id : ''

  // Trần chiết khấu (D25) theo bảng giá CTV sắp gán: mức cao nhất ≤ chênh lệch nhỏ nhất của bạn.
  const capQ = useQuery({
    queryKey: ['commissions', 'cap-preview', 'price-list', priceId, chosenScheme?.id],
    queryFn: () => get<{ cap: CapCheck | null }>(`/commissions/cap-preview?priceListId=${encodeURIComponent(priceId)}&schemeId=${encodeURIComponent(chosenScheme!.id)}`),
    enabled: withScheme && !!priceId && !!chosenScheme,
    staleTime: 0,
  })
  const cap = capQ.data?.cap ?? null
  const capBlocked = withScheme && !!chosenScheme && !!priceId && !!cap && !cap.ok
  const handleProfileIncomplete = useProfileIncompleteHandler()

  // Chủ tài khoản cấp dưới phải đủ họ tên + email ngay khi tạo (hồ sơ bắt buộc).
  const [submitted, setSubmitted] = useState(false)
  const ownerNameError = !form.ownerName.trim() ? 'Nhập họ tên chủ tài khoản' : null
  const ownerEmailError = !form.ownerEmail.trim()
    ? 'Nhập email chủ tài khoản'
    : !EMAIL_RE.test(form.ownerEmail.trim())
      ? 'Email không đúng định dạng'
      : null
  const priceVal = setupValue(price, 'bảng giá')
  const schemeVal = withScheme ? setupValue(scheme, 'bảng chiết khấu') : { id: null, error: null }

  const create = useMutation({
    mutationFn: async () => {
      if (!form.name.trim()) throw new Error('Vui lòng nhập tên')
      if (priceVal.error) throw new Error(priceVal.error)
      if (schemeVal.error) throw new Error(schemeVal.error)
      if (ownerNameError) throw new Error(ownerNameError)
      if (!isValidVnPhone(form.ownerPhone)) throw new Error('Số điện thoại chủ tài khoản không đúng định dạng di động VN')
      if (ownerEmailError) throw new Error(ownerEmailError)
      if (form.password.length < 8) throw new Error('Mật khẩu tối thiểu 8 ký tự')
      if (capBlocked) throw new Error('Bảng chiết khấu vượt trần')
      return post<{ unit: { id: string; name: string }; warning: string | null }>('/network/units', {
        unitType: form.unitType,
        name: form.name.trim(),
        ownerName: form.ownerName.trim(),
        ownerPhone: normalizePhoneVN(form.ownerPhone),
        ownerEmail: form.ownerEmail.trim().toLowerCase(),
        password: form.password,
        priceListId: priceVal.id,
        ...(withScheme ? { schemeId: schemeVal.id } : {}),
        ...(form.unitType === 'ctv' ? { workModes: form.workModes } : {}),
      })
    },
    onSuccess: (r) => {
      toast.success(`Đã tạo ${TYPE_LABEL[form.unitType]} "${r.unit.name}"`)
      if (r.warning) toast.error(r.warning)
      qc.invalidateQueries({ queryKey: ['network-units'] })
      qc.invalidateQueries({ queryKey: ['network-price-lists'] })
      if (withScheme) qc.invalidateQueries({ queryKey: ['commissions'] })
      onDone()
    },
    onError: (e) => {
      if (!handleProfileIncomplete(e)) toast.error((e as Error).message)
    },
  })

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        setSubmitted(true)
        create.mutate()
      }}
      className="space-y-4"
    >
      {childTypes.length > 1 && (
        <div className="flex gap-2">
          {childTypes.map((t) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant={form.unitType === t ? 'default' : 'outline'}
              onClick={() => {
                setForm({ ...form, unitType: t })
                // Bảng giá phải đúng loại — đổi loại thì chọn lại
                setPrice(null)
              }}
            >
              {TYPE_LABEL[t]}
            </Button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4">
        <Field label={`Tên ${TYPE_LABEL[form.unitType]}`}>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <SetupPicker
          label="Bảng giá"
          what="bảng giá"
          value={price}
          onChange={setPrice}
          options={options.map((l) => ({ id: l.id, label: `${l.name} (${l.code}) · ${l.unitCount} đơn vị đang dùng` }))}
          canCreate={canPricing}
          onCreate={() => setMaking('price')}
          laterWarning={priceLaterWarning(form.unitType)}
          error={submitted ? priceVal.error : null}
          disabled={create.isPending}
        />
        {withScheme &&
          (schemesError ? (
            <p className="text-sm text-warn">Không tải được bảng chiết khấu.</p>
          ) : (
            <SetupPicker
              label="Bảng chiết khấu"
              what="bảng chiết khấu"
              value={scheme}
              onChange={setScheme}
              options={schemes.map((x) => ({ id: x.id, label: `${x.name} (${x.code}) · ${MODE_SHORT[x.mode]}` }))}
              canCreate={canPricing}
              onCreate={() => setMaking('scheme')}
              laterWarning={schemeLaterWarning()}
              error={submitted ? schemeVal.error : null}
              disabled={create.isPending}
            />
          ))}
        <Field label="Họ tên chủ tài khoản" error={submitted ? ownerNameError : null}>
          <Input
            required
            maxLength={100}
            autoComplete="off"
            value={form.ownerName}
            onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
            placeholder="VD: Nguyễn Mai Anh"
            aria-invalid={submitted && ownerNameError ? true : undefined}
          />
        </Field>
        <Field label="SĐT đăng nhập">
          <PhoneInput required value={form.ownerPhone} onValueChange={(v) => setForm({ ...form, ownerPhone: v || '' })} showValidation />
        </Field>
        <Field label="Email chủ tài khoản" error={submitted ? ownerEmailError : null}>
          <Input
            required
            type="email"
            inputMode="email"
            autoComplete="off"
            maxLength={254}
            value={form.ownerEmail}
            onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
            placeholder="ten@vidu.com"
            aria-invalid={submitted && ownerEmailError ? true : undefined}
          />
        </Field>
        <Field label="Mật khẩu ban đầu">
          <Input
            required
            type="text"
            autoComplete="new-password"
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Tối thiểu 8 ký tự"
          />
        </Field>
      </div>
      {form.unitType === 'ctv' && <WorkModePicker value={form.workModes} onChange={(workModes) => setForm({ ...form, workModes })} disabled={create.isPending} />}
      {withScheme && chosenScheme && (
        <div aria-live="polite" className="space-y-1.5 text-sm">
          <p className="text-xs text-inkSoft">{tiersSummary(versionAt(chosenScheme, currentMonthVN())?.tiers ?? chosenScheme.tiers)}</p>
          {!priceId ? null : capQ.isLoading ? (
            <p className="rounded-lg bg-paperSoft px-3 py-2 text-inkSoft">Đang kiểm tra…</p>
          ) : capQ.isError ? (
            <p className="rounded-lg bg-warn/10 px-3 py-2 text-ink">Chưa kiểm tra được trần chiết khấu.</p>
          ) : cap && !cap.ok ? (
            <p className="flex gap-2 rounded-lg bg-danger/5 px-3 py-2 text-danger">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                Vượt trần: mức cao nhất {vnd(cap.maxRate)}/đơn lớn hơn chênh lệch nhỏ nhất {vnd(cap.minMargin)}
                {cap.at ? ` (ô ${cap.at})` : ''}.
              </span>
            </p>
          ) : cap ? (
            <p className="flex gap-2 rounded-lg bg-ok/10 px-3 py-2 text-ink">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" />
              <span>
                Hợp lệ: mỗi đơn bạn còn ít nhất {vnd(cap.minMargin - cap.maxRate)}.
              </span>
            </p>
          ) : (
            <p className="rounded-lg bg-paperSoft px-3 py-2 text-inkSoft">Chưa tính được trần (bạn chưa có giá nhập).</p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2">
        <Button type="submit" disabled={create.isPending || capBlocked}>
          {create.isPending ? 'Đang tạo…' : `Tạo ${TYPE_LABEL[form.unitType]}`}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={create.isPending}>
          Hủy
        </Button>
      </div>
      {making === 'price' && (
        <NewPriceListDialog
          kind={form.unitType}
          onClose={() => setMaking(null)}
          onCreated={(l) => {
            setMaking(null)
            setPrice({ mode: 'pick', id: l.id })
          }}
        />
      )}
      {making === 'scheme' && (
        <NewSchemeDialog
          onClose={() => setMaking(null)}
          onCreated={(x) => {
            setMaking(null)
            setScheme({ mode: 'pick', id: x.id })
          }}
        />
      )}
    </form>
  )
}

function Field({ label, error, children }: { label: string; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm text-inkSoft">{label}</span>
      {children}
      {error && <span className="block text-xs text-danger" role="alert">{error}</span>}
    </label>
  )
}
